
/**
 * Module depenencies.
 */

import { CollectionProps } from 'src/types/collection';
import { MagazineProps } from 'src/types/magazine';
import { ProductProps } from 'src/types/products';
import { ProjectProps } from 'src/types/projects';
import { breakpoints } from 'src/styles/breakpoints';
import { color, media, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import { kioskRoutes } from 'src/core/routes';
import { routeTemplate, useLocalizedRoute } from 'src/core/utils/routes';
import CollectionCard from 'src/components/cards/collection-card';
import Container from 'src/components/core/layout/container';
import Label from 'src/components/core/typography/label';
import MagazineCard from 'src/components/cards/magazine-card';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import ProductCard from 'src/components/cards/product-card';
import ProjectCard from 'src/components/cards/project-card';
import React, { ReactElement, useRef } from 'react';
import Slider, { Slide } from 'src/components/core/slider';
import SliderControls from 'src/components/core/slider/slider-controls';
import map from 'lodash/map';
import size from 'lodash/size';
import styled, { css } from 'styled-components';

/**
 * `GalleryType` type.
 */

type GalleryType = 'collection' | 'magazine'| 'product' | 'project';

/**
 * `Props` type.
 */

type Props = ({
  items: CollectionProps[],
  type: 'collection'
} | {
  items: ProductProps[],
  type: 'product'
} | {
  items: ProjectProps[],
  type: 'project'
} | {
  items: MagazineProps[],
  type: 'magazine'
}) & {
  className?: string,
  isKiosk?: boolean
  label?: string
};

/**
 * `Section` component.
 */

const Section = styled.section`
  background-color: ${color('white')};
  padding: ${units(13)} 0;
  position: relative;

  ${media.min('ms')`
    padding: ${units(25)} 0;
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding-right: 0;

  ${media.min('ms')`
    padding-right: 0;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. gallery   gallery'
      '. scrollbar .';
    grid-template-columns: calc(100% / 12) calc(100% / 12 * 10) calc(100% / 12);
  `}
`;

/**
 * `Gallery` styled component.
 */

const Gallery = styled.div`
  margin-bottom: ${units(6)};
  padding-left: ${units(6)};
  position: relative;
  width: 100%;

  ${media.min('ms')`
    grid-area: gallery;
    margin-bottom: 0;
  `}
`;

/**
 * `StyledLabel` styled component.
 */

const StyledLabel = styled(Label).attrs({ size: 'small' })`
  color: ${color('brown400')};
  font-style: italic;
  font-weight: 500;
  left: 0;
  position: absolute;
  top: 0;
  transform: rotate(-90deg) translate(-100%, 0);
  transform-origin: 0 0;
`;

/**
 * `ScrollbarWrapper` styled component.
 */

const ScrollbarWrapper = styled.div<{ isKiosk?: boolean }>`
  ${media.min('ms')`
    grid-area: scrollbar;

    ${ifProp('isKiosk', css`
      margin-bottom: ${units(10)};
    `)}
  `}
`;

/**
 * `StyledSliderControls` styled component.
 */

const StyledSliderControls = styled(SliderControls)`
  padding: 0 ${theme('grid.gutterMobile')}px 0 0;

  ${media.min('ms')`
    padding-right: ${theme('grid.gutter')}px;
    padding-top: ${units(3)};
  `}
`;

/**
 * Get carousel config.
 */

function getCarouselConfig(type: GalleryType) {
  switch (type) {
    case 'product':
      return {
        breakpoints: {
          [breakpoints.sm]: {
            slidesPerView: 1.5,
            spaceBetween: 24
          },
          [breakpoints.ms]: {
            centeredSlides: true,
            slidesPerView: 1.75,
            spaceBetween: 32
          },
          [breakpoints.md]: {
            centeredSlides: true,
            slidesPerView: 2.75,
            spaceBetween: 40
          },
          [breakpoints.lg]: {
            centeredSlides: true,
            slidesPerView: 2.75,
            spaceBetween: 60
          },
          [breakpoints.xl]: {
            centeredSlides: true,
            slidesPerView: 2.75,
            spaceBetween: 80
          }
        },
        slidesPerView: 1.25,
        spaceBetween: 24
      };

    default:
      return {
        breakpoints: {
          [breakpoints.sm]: {
            slidesPerView: 1.5,
            spaceBetween: 20
          },
          [breakpoints.ms]: {
            centeredSlides: true,
            slidesPerView: 1.75,
            spaceBetween: 30
          },
          [breakpoints.md]: {
            centeredSlides: true,
            slidesPerView: 2.75,
            spaceBetween: 30
          },
          [breakpoints.lg]: {
            centeredSlides: true,
            slidesPerView: 3.5,
            spaceBetween: 40
          },
          [breakpoints.xl]: {
            centeredSlides: true,
            slidesPerView: 4,
            spaceBetween: 40
          }
        },
        slidesPerView: 1.25,
        spaceBetween: 20
      };
  }
}

/**
 * `RelatedGallery` component.
 */

const RelatedGallery = (props: Props): ReactElement | null => {
  const { className, isKiosk, items, label, type } = props;
  const total = size(items);
  const sliderRef = useRef<any>();
  const routeResolver = useLocalizedRoute();

  if (total === 0) {
    return null;
  }

  return (
    <Section className={className}>
      <StyledContainer
        fluid
        hasPadding={!isKiosk}
      >
        <Grid>
          <Gallery>
            {label && (
              <StyledLabel>
                {label}
              </StyledLabel>
            )}

            <ParallaxFadeInUp
              inViewOptions={{ threshold: 0.1 }}
              parallaxOptions={{ speed: 3 }}
            >
              <Slider
                {...getCarouselConfig(type)}
                ref={sliderRef}
              >
                {map(items, (item, index: number) => {
                  const kioskProductRoute = routeTemplate(kioskRoutes.productDetails, { slug: item.slug });
                  const productRoute = routeResolver('productDetails', { slug: item.slug });

                  return (
                    <Slide key={index}>
                      {type === 'collection' && (
                        <CollectionCard
                          {...item}
                          bgColorTheme={'grey'}
                          isKioskCard={isKiosk}
                        />
                      )}

                      {type === 'product' && (
                        <ProductCard
                          isKioskCard={isKiosk}
                          url={isKiosk ? kioskProductRoute : productRoute}
                          {...item}
                        />
                      )}

                      {type === 'project' && (
                        <ProjectCard
                          isKioskCard={isKiosk}
                          url={routeResolver('portfolio', { slug: item.slug })}
                          {...item}
                        />
                      )}

                      {type === 'magazine' && (
                        <MagazineCard
                          url={routeResolver('storytiling', { slug: item.slug })}
                          {...item}
                        />
                      )}
                    </Slide>
                  );
                })}
              </Slider>
            </ParallaxFadeInUp>
          </Gallery>

          <ScrollbarWrapper isKiosk={isKiosk}>
            <ParallaxFadeInUp parallaxOptions={{ speed: 3 }}>
              <StyledSliderControls
                sliderRef={sliderRef}
                total={total}
              />
            </ParallaxFadeInUp>
          </ScrollbarWrapper>
        </Grid>
      </StyledContainer>
    </Section>
  );
};

/**
 * Export `RelatedGallery` component.
 */

export default RelatedGallery;
